import { Spacing, Title } from '@vkontakte/vkui';
import { requestPayment } from 'Cloud/Application/Payment';
import classNames from 'clsx';
import React, { memo, ReactElement, useCallback, useMemo } from 'react';
import { useGoToSubscriptions } from 'reactApp/hooks/useGoToSubscriptions';
import { Product, Tariff } from 'reactApp/types/Billing';
import Checkbox from 'reactApp/ui/CheckBox/CheckBox';
import { DiscountLabel } from 'reactApp/ui/DiscountLabel/DiscountLabel';
import { Switch } from 'reactApp/ui/Switch/Switch';
import { getDiscount } from 'reactApp/utils/getDiscount';
import {
    getPeriodName,
    getTariffProductsByPeriods,
    isMonth6Period,
    isMonthPeriod,
    isYear2Period,
    isYearPeriod,
} from 'reactApp/utils/Period';
import { formatPrice, getMonthProductPrice } from 'reactApp/utils/priceHelpers';

import styles from './FastCheckoutSidebar.css';

export const FastCheckoutSidebar = memo(
    ({
        tariff,
        product,
        onProductChange,
        onClickTariffs,
    }: {
        tariff: Tariff;
        product: Product;
        onProductChange: (id: string) => void;
        onClickTariffs: () => void;
    }): ReactElement => {
        const { goToSubscriptions } = useGoToSubscriptions();

        // tempexp_14791-start
        const hasTwoYearProduct = tariff?.products.some((item) => isYear2Period(item.period));

        const isPro = (product?.isProfessional && !product?.isSingleQuota) || (product?.isSingleQuota && product.hasDiskO);

        const { yearProduct, monthProduct } = getTariffProductsByPeriods(tariff?.products, isPro, hasTwoYearProduct);
        // tempexp_14791-end

        const { space, discountPeriod, period, discountPrice, price, hasDiscount } = product;

        const { roundedDiscount } = getDiscount({ discountPrice: discountPrice || 0, price });

        const isYear = isYearPeriod(period);

        const switchChange = useCallback(() => {
            if (monthProduct && yearProduct) {
                onProductChange(isYear ? monthProduct.id : yearProduct.id);
            }
        }, [isYear, monthProduct, onProductChange, yearProduct]);

        const clickTariffs = useCallback(() => {
            onClickTariffs();
            goToSubscriptions({ inSameTab: false });

            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            requestPayment({ showTariffList: true, forceFastCheckout: true, onProductSelect: onProductChange });
        }, [onClickTariffs, goToSubscriptions, onProductChange]);

        const priceBlock = useMemo(() => {
            if (hasDiscount && discountPrice && discountPeriod) {
                return (
                    <div className={styles.discountBlock}>
                        <div className={styles.oldPrice}>
                            {formatPrice(price)} ₽/{getPeriodName(period)}
                        </div>
                        <div className={styles.price}>
                            {formatPrice(discountPrice)} ₽/{getPeriodName(discountPeriod)}
                        </div>
                    </div>
                );
            }

            if (isYearPeriod(period) || isMonth6Period(period)) {
                return (
                    <div className={styles.priceBlock}>
                        <div className={styles.priceInfo}>
                            Подписка на {getPeriodName(period)} по цене {formatPrice(price)}&nbsp;₽
                        </div>
                        <div className={styles.price}>{formatPrice(getMonthProductPrice(product))} ₽/месяц</div>
                    </div>
                );
            }

            if (isMonthPeriod(period)) {
                return (
                    <div className={styles.priceBlock}>
                        <div className={styles.priceInfo}>Подписка на месяц по цене</div>
                        <div className={styles.price}>{formatPrice(price)} ₽/месяц</div>
                    </div>
                );
            }
        }, [discountPeriod, discountPrice, hasDiscount, period, price, product]);

        // tempexp_14791-start
        const switchBlock = useMemo(
            () =>
                tariff?.products?.length > 1 &&
                monthProduct && (
                    <div className={styles.switch} onClick={switchChange}>
                        <div
                            className={classNames({
                                [styles.switchLabel]: true,
                                [styles.switchLabel_active]: isYear,
                            })}
                        >
                            На год
                        </div>
                        <Switch name={`tariff_${product.id}`} checked={!isYear} theme="big" />
                        <div
                            className={classNames({
                                [styles.switchLabel]: true,
                                [styles.switchLabel_right]: true,
                                [styles.switchLabel_active]: !isYear,
                            })}
                        >
                            На {getPeriodName(monthProduct.period)}
                        </div>
                    </div>
                ),
            [isYear, monthProduct, product.id, switchChange, tariff?.products?.length]
        );
        // tempexp_14791-end

        const radioBlock = useMemo(
            () =>
                tariff?.products?.length > 1 &&
                yearProduct &&
                monthProduct && (
                    <>
                        <div className={styles.radioBlock} onClick={switchChange}>
                            <div className={styles.radio}>
                                <Checkbox selected={!isYear} mod="round" />
                            </div>
                            <Title level="3" weight="2">
                                {formatPrice(getMonthProductPrice(monthProduct))} ₽ в месяц{' '}
                            </Title>
                        </div>
                        <Spacing size={16} />
                        <div className={styles.radioBlock} onClick={switchChange}>
                            <div className={styles.radio}>
                                <Checkbox selected={isYear} mod="round" />
                            </div>
                            <div>
                                <div className={styles.twoYearTitle}>
                                    <Title level="3" weight="2">
                                        {formatPrice(getMonthProductPrice(yearProduct))} ₽ в месяц{' '}
                                    </Title>
                                    <div className={styles.discountLabel}>
                                        <DiscountLabel tariff={tariff} />
                                    </div>
                                </div>

                                <div className={styles.productInfo}>
                                    купить на {getPeriodName(yearProduct.period)} за {formatPrice(yearProduct.price)} ₽
                                </div>
                            </div>
                        </div>
                        <Spacing size={42} />
                    </>
                ),
            [isYear, monthProduct, switchChange, tariff?.products?.length, yearProduct]
        );

        const features = {
            singleQuota: [
                'Без рекламы в\u00A0Почте\u00A0и\u00A0Облаке',
                'Загрузка файлов до 100 ГБ в\u00A0Облако',
                'Отправка файлов до 100 ГБ в\u00A0письмах',
                'Автоудаление папок',
                'Приоритетная поддержка',
            ],
            // В теории возможно что тариф будет не единоквотным, по этому на всякий случай оставляю
            default: ['Никакой рекламы', 'Загрузка файлов до 32 ГБ', 'Автоудаление папок', 'Приоритетная поддержка'],
        };

        return (
            <div className={styles.root}>
                <div className={styles.tariffInfo}>
                    {hasDiscount && !!roundedDiscount && <div className={styles.label}>Скидка {roundedDiscount}%</div>}
                    <div className={styles.header}>
                        <div className={styles.quota}>+{space.space}</div>
                        <div className={styles.units}>{space.units}</div>
                    </div>

                    {/* tempexp_14791-start */}
                    {hasTwoYearProduct ? (
                        radioBlock
                    ) : (
                        <>
                            {switchBlock}
                            {priceBlock}
                        </>
                    )}
                    {/* tempexp_14791-end */}

                    <ol className={styles.features}>
                        {features[tariff.isSingleQuota ? 'singleQuota' : 'default'].map((text, index) => (
                            <li key={index}>{text}</li>
                        ))}
                    </ol>
                </div>
                <div className={styles.tariffsLink} onClick={clickTariffs}>
                    Другие тарифы
                </div>
            </div>
        );
    }
);

FastCheckoutSidebar.displayName = 'FastCheckoutSidebar';
